import { useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Mail, Smartphone } from 'lucide-react';
import { Button, Skeleton, cn } from '@hrms/ui';
import type { SsoProvider, SsoPurpose } from '@hrms/shared';
import { tsr } from '../../lib/api.js';
import { isNativeApp } from '../../lib/nativeBrowser.js';
import { beginNativeSso } from '../../lib/nativeSso.js';
import {
  SSO_PROVIDER_LABELS,
  SSO_PROVIDER_LOGO_IS_WORDMARK,
  SSO_PROVIDER_LOGOS,
  ssoSignInAvailableHere,
  ssoStartHref,
} from '../../lib/ssoSignIn.js';

/**
 * The first screen of sign-in and sign-up: one button per identity provider the SERVER says is
 * configured ("Continue with Zoho", "Continue with Google"), an "or", and "Continue with email".
 * Choosing email shows the page's own email + password form.
 *
 * Where no provider is configured the pages skip this screen and show the email form straight away, so
 * nobody is asked to pick from a one-item menu.
 *
 * In the packaged phone app the provider button opens the system browser (lib/nativeSso.ts) rather
 * than navigating the WebView — Google refuses embedded WebViews — and the sign-in returns to the app
 * over `peopleorbit://sso`. Email + password stays inside the app.
 */

/**
 * Which providers this page can offer, and whether the server has answered yet. The list is a public
 * query, so it loads in the phone app too.
 */
export function useSsoProviders(): { providers: readonly SsoProvider[]; loading: boolean } {
  const available = ssoSignInAvailableHere();
  const query = tsr.auth.ssoProviders.useQuery({
    queryKey: ['auth', 'sso-providers'],
    staleTime: 5 * 60_000,
    enabled: available,
  });
  if (!available) return { providers: [], loading: false };
  if (query.isLoading) return { providers: [], loading: true };
  return { providers: query.data?.status === 200 ? query.data.body.providers : [], loading: false };
}

const METHOD_PARAM = 'method';

/**
 * The chosen sign-in method lives in the URL (`?method=email`), so the browser's Back button
 * returns to the choice and a link can open the email form directly. Other query params are kept.
 *
 * A freshly installed phone app has no stored session, so RequireAuth lands it straight on this
 * screen (sign-up, usually) — the provider chooser would be the very first thing a new person
 * sees, one more tap (and a trip through the system browser) before an actual form. So the phone
 * app defaults to the email/phone form instead; Zoho/Google stay one tap away via "Other sign-up
 * options". The web keeps defaulting to the chooser, unchanged. An explicit `?method=` always wins.
 */
export function useAuthMethod(): {
  method: 'choose' | 'email';
  chooseEmail: () => void;
  backToChoices: () => void;
} {
  const [params, setParams] = useSearchParams();
  // The router state carries where to go after signing in (RequireAuth's `from` — a shared course
  // link, a deep link). Changing the query string is a navigation, which would drop it: pass it on.
  const { state } = useLocation();
  const methodParam = params.get(METHOD_PARAM);
  const method: 'choose' | 'email' =
    methodParam === 'email' ? 'email' : methodParam === 'choose' ? 'choose' : isNativeApp() ? 'email' : 'choose';
  return {
    method,
    chooseEmail: () => {
      const next = new URLSearchParams(params);
      next.set(METHOD_PARAM, 'email');
      setParams(next, { state });
    },
    backToChoices: () => {
      const next = new URLSearchParams(params);
      // On the web the default is already "choose", so clearing the param is enough. The phone
      // app's default is "email" (above), so going back has to set the param explicitly or it
      // would just land back on email.
      if (isNativeApp()) next.set(METHOD_PARAM, 'choose');
      else next.delete(METHOD_PARAM);
      setParams(next, { state });
    },
  };
}

export function AuthMethodChooser({
  purpose,
  providers,
  loading,
  orgSlug,
  next,
  onChooseEmail,
}: {
  purpose: SsoPurpose;
  providers: readonly SsoProvider[];
  /** The provider list is still loading — a placeholder holds the provider button's place. */
  loading: boolean;
  /** The workspace already in view (a company's own subdomain), which narrows a sign-in to it. */
  orgSlug?: string | null;
  next?: string;
  onChooseEmail: () => void;
}) {
  const verb = purpose === 'signup' ? 'Sign up with' : 'Continue with';
  const [openFailed, setOpenFailed] = useState(false);
  // Two quick taps would make two PKCE pairs, the second replacing the first one's verifier.
  const [opening, setOpening] = useState(false);

  function chooseProvider(provider: SsoProvider) {
    setOpenFailed(false);
    if (!isNativeApp()) {
      window.location.assign(ssoStartHref(provider, purpose, { orgSlug, next }));
      return;
    }
    // The phone app: the provider flow runs in the system browser and returns over peopleorbit://sso.
    setOpening(true);
    beginNativeSso(provider, purpose, { orgSlug, next })
      .catch(() => setOpenFailed(true))
      .finally(() => setOpening(false));
  }

  return (
    <div className="flex flex-col gap-3">
      {loading ? (
        <Skeleton className="h-11 w-full rounded-lg" />
      ) : (
        providers.map((provider) => (
          <Button
            key={provider}
            type="button"
            variant="secondary"
            size="lg"
            className="h-11 w-full rounded-lg font-semibold shadow-sm"
            disabled={opening}
            onClick={() => chooseProvider(provider)}
          >
            {SSO_PROVIDER_LOGO_IS_WORDMARK[provider] ? (
              <span className="inline-flex items-center gap-2">
                {verb}
                <img
                  src={SSO_PROVIDER_LOGOS[provider]}
                  alt={SSO_PROVIDER_LABELS[provider]}
                  className="h-4 w-auto"
                />
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <img src={SSO_PROVIDER_LOGOS[provider]} alt="" className="size-4" />
                {verb} {SSO_PROVIDER_LABELS[provider]}
              </span>
            )}
          </Button>
        ))
      )}
      {openFailed && (
        <p role="alert" className="text-sm text-danger">
          Couldn’t open the browser for sign-in. Try again, or continue with{' '}
          {purpose === 'signup' ? 'your phone number' : 'email'}.
        </p>
      )}
      {(loading || providers.length > 0) && (
        <div
          role="separator"
          aria-label="or"
          className="flex items-center gap-3 text-xs font-medium uppercase tracking-wider text-fg-subtle"
        >
          <span className="h-px flex-1 bg-border" aria-hidden="true" />
          or
          <span className="h-px flex-1 bg-border" aria-hidden="true" />
        </div>
      )}
      <Button
        type="button"
        variant="secondary"
        size="lg"
        className="h-11 w-full rounded-lg font-semibold shadow-sm"
        onClick={onChooseEmail}
      >
        {/* Sign-up's own form starts with the mobile number (email comes later, on the company step),
         * so the button names what it opens; sign-in's form takes an email or mobile and a password. */}
        {purpose === 'signup' ? (
          <Smartphone className="size-4" aria-hidden="true" />
        ) : (
          <Mail className="size-4" aria-hidden="true" />
        )}
        {verb} {purpose === 'signup' ? 'phone number' : 'email'}
      </Button>
    </div>
  );
}

/** "← Other sign-in options" above the email form, when there are other options to go back to. */
export function BackToMethods({
  purpose,
  onClick,
  className,
}: {
  purpose: SsoPurpose;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex min-h-10 items-center gap-1.5 self-start text-sm font-medium text-fg-muted hover:text-fg',
        className,
      )}
    >
      <ArrowLeft className="size-4 rtl:rotate-180" aria-hidden="true" />
      {purpose === 'signup' ? 'Other sign-up options' : 'Other sign-in options'}
    </button>
  );
}
